import { useState, useEffect, useRef, useMemo, memo, type FC, type PropsWithChildren, useTransition, useReducer } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useGenie } from '../context/GenieContext';
import type { ChartRecommendation } from '../context/GenieContext';
import {
    ResponsiveContainer,
    LineChart, Line,
    BarChart, Bar,
    AreaChart, Area,
    ScatterChart, Scatter,
    PieChart, Pie, Cell,
    XAxis, YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label
} from 'recharts';
import html2canvas from 'html2canvas';

interface TableRendererProps extends PropsWithChildren {
    messageId?: string;
    attachmentId?: string;
    chartRecommendation?: ChartRecommendation;
}

interface ChartRendererProps {
    data: any[];
    columns: string[];
    recommendation: ChartRecommendation;
    isAnimationActive?: boolean;
}

const CHART_COLORS = ['#8E1F20', '#003366', '#28a745', '#ffc107', '#17a2b8', '#fd7e14', '#6610f2', '#e83e8c', '#20c997', '#6c757d'];

/** Returns true when a column name suggests it is a label/identifier rather than a measure. */
const LABEL_COLUMN_PATTERNS = /\b(year|month|day|quarter|week|date|id|code|zip|postal|phone|fips|number|num|no)\b/i;
const isLabelColumn = (colName?: string): boolean => !!colName && LABEL_COLUMN_PATTERNS.test(colName);

const formatNumber = (val: any, colName?: string) => {
    if (typeof val === 'number') {
        // Label columns (year, month, id, etc.) should never get separators or suffixes
        if (isLabelColumn(colName)) return val.toString();
        const absVal = Math.abs(val);
        if (absVal >= 1000000000) return (val / 1000000000).toFixed(2).replace(/\.00$/, '').replace(/(\.[1-9])0$/, '$1') + 'B';
        if (absVal >= 1000000) return (val / 1000000).toFixed(2).replace(/\.00$/, '').replace(/(\.[1-9])0$/, '$1') + 'M';
        if (absVal >= 1000) return (val / 1000).toFixed(2).replace(/\.00$/, '').replace(/(\.[1-9])0$/, '$1') + 'k';
        return val.toLocaleString();
    }
    return val;
};

const formatXAxis = (tick: any) => {
    if (typeof tick === 'string' && /^\d{4}-\d{2}$/.test(tick)) {
        const [y, m] = tick.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[parseInt(m, 10) - 1]} ${y}`;
    }
    return tick;
};

const CopyButton: FC<{ text: string; style?: React.CSSProperties; className?: string }> = ({ text, style, className }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button
            className={`message-copy-btn ${className || ''}`}
            onClick={handleCopy}
            title="Copy message"
            style={{
                border: 'none',
                background: 'rgba(255, 255, 255, 0.15)',
                color: 'inherit',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.8rem',
                cursor: 'pointer',
                opacity: 0,
                transition: 'opacity 0.2s, background 0.2s',
                zIndex: 10,
                ...style
            }}
        >
            <i className={`fa-solid ${copied ? 'fa-check text-success' : 'fa-copy'}`}></i>
        </button>
    );
};

const ChartRenderer: FC<ChartRendererProps> = memo(({ data, columns, recommendation, isAnimationActive = true }) => {
    const { chart_type, x_axis, x_axis_label, y_axis, y_axis_label, y2_axis, y2_axis_label, legend: legendCfg, chart_title, color_column } = recommendation;
    const series = columns.filter(c => c !== x_axis);
    const margin = useMemo(() => ({ top: 20, right: 20, left: 60, bottom: 60 }), []);
    const showLegend = legendCfg?.show ?? series.length > 1;

    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
    const toggleSeries = (dataKey: any) => {
        const key = typeof dataKey === 'string' ? dataKey : dataKey.value;
        const totalSeriesCount = chart_type === 'combo' ? (y2_axis ? 2 : 1) : (chart_type === 'horizontal_bar' ? columns.filter(c => c !== (color_column && !columns.includes(y_axis) ? x_axis : y_axis)).length : series.length);

        setHiddenSeries(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else if (next.size < totalSeriesCount - 1) {
                // Only allow hiding if at least one series remains visible
                next.add(key);
            }
            return next;
        });
    };

    const legendProps = showLegend ? {
        wrapperStyle: { paddingTop: '20px', maxHeight: '150px', overflowY: 'auto' as const },
        onClick: toggleSeries,
        formatter: (value: string) => (
            <span style={{
                color: hiddenSeries.has(value) ? '#ccc' : '#333',
                fontWeight: 500,
                fontSize: '11px',
                textDecoration: hiddenSeries.has(value) ? 'line-through' : 'none',
                cursor: 'pointer'
            }}>
                {value}
            </span>
        ),
        content: (props: any) => {
            const { payload } = props;
            if (!payload) return null;
            return (
                <div className="custom-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: (legendCfg?.position === 'right' ? 'flex-start' : 'center'), alignItems: 'center', padding: '10px' }}>
                    {legendCfg?.title && <div style={{ fontWeight: 600, fontSize: '12px', marginRight: '10px', color: '#444' }}>{legendCfg.title}:</div>}
                    {payload.map((entry: any) => {
                        const isHidden = hiddenSeries.has(entry.value);
                        return (
                            <div
                                key={entry.value}
                                onClick={() => toggleSeries(entry.value)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    opacity: isHidden ? 0.4 : 1,
                                    transition: 'opacity 0.2s'
                                }}
                            >
                                <div style={{ width: 10, height: 10, backgroundColor: isHidden ? '#ccc' : entry.color, borderRadius: '50%' }}></div>
                                <span style={{ color: isHidden ? '#999' : '#333', fontWeight: 500, fontSize: '11px', textDecoration: isHidden ? 'line-through' : 'none' }}>{entry.value}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }
    } : undefined;

    const tooltipStyle = { borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '350px', overflowY: 'auto' as const };

    // --- LINE ---
    if (chart_type === 'line') {
        return (
            <div style={{ width: '100%', height: 500, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <LineChart data={data} margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey={x_axis} tick={{ fontSize: 10, fill: '#666' }} tickFormatter={formatXAxis} height={60} interval={0} angle={-45} textAnchor="end" tickMargin={10}>
                            <Label value={x_axis_label || x_axis} position="insideBottom" offset={-20} style={{ fontSize: '11px', fill: '#666', fontWeight: 600 }} />
                        </XAxis>
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70}>
                            <Label value={y_axis_label || y_axis} angle={-90} position="center" style={{ fontSize: '12px', fill: '#444', fontWeight: 600, textAnchor: 'middle' }} dx={-30} />
                        </YAxis>
                        <Tooltip formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} wrapperStyle={{ pointerEvents: 'auto' }} />
                        {showLegend && <Legend {...legendProps} iconType="plainline" />}
                        {series.map((s, i) => (
                            <Line key={s} type="monotone" dataKey={s} name={s} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls legendType="plainline" hide={hiddenSeries.has(s)} isAnimationActive={isAnimationActive} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- BAR / STACKED_BAR / GROUPED_BAR ---
    if (['bar', 'stacked_bar', 'grouped_bar'].includes(chart_type)) {
        const useStack = chart_type === 'stacked_bar';
        return (
            <div style={{ width: '100%', height: 500, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={data} margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey={x_axis} tick={{ fontSize: 10, fill: '#666' }} tickFormatter={formatXAxis} height={60} interval={0} angle={-45} textAnchor="end" tickMargin={10}>
                            <Label value={x_axis_label || x_axis} position="insideBottom" offset={-20} style={{ fontSize: '11px', fill: '#666', fontWeight: 600 }} />
                        </XAxis>
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70}>
                            <Label value={y_axis_label || y_axis} angle={-90} position="center" style={{ fontSize: '12px', fill: '#444', fontWeight: 600, textAnchor: 'middle' }} dx={-30} />
                        </YAxis>
                        <Tooltip cursor={{ fill: '#f5f5f5' }} formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} wrapperStyle={{ pointerEvents: 'auto' }} />
                        {showLegend && <Legend {...legendProps} />}
                        {series.map((s, i) => (
                            <Bar key={s} dataKey={s} name={s} fill={CHART_COLORS[i % CHART_COLORS.length]} stackId={useStack ? 'stack' : undefined} hide={hiddenSeries.has(s)} isAnimationActive={isAnimationActive} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- HORIZONTAL_BAR ---
    if (chart_type === 'horizontal_bar') {
        // When color_column is set, data has been pivoted: x_axis is the category key, 
        // the original y_axis (measure) was spread into columns named after color_column values.
        // When no color_column, y_axis is the category (vertical axis).
        const isPivoted = !!color_column && !columns.includes(y_axis);
        const categoryKey = isPivoted ? x_axis : y_axis;
        const hSeries = columns.filter(c => c !== categoryKey);
        const xLabel = isPivoted ? (y_axis_label || y_axis) : (x_axis_label || x_axis);
        return (
            <div style={{ width: '100%', height: Math.max(400, data.length * 35), marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={data} margin={{ ...margin, left: 160 }} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)}>
                            <Label value={xLabel} position="insideBottom" offset={-10} style={{ fontSize: '11px', fill: '#666', fontWeight: 600 }} />
                        </XAxis>
                        <YAxis type="category" dataKey={categoryKey} tick={{ fontSize: 10, fill: '#666' }} width={160} />
                        <Tooltip formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} wrapperStyle={{ pointerEvents: 'auto' }} />
                        {showLegend && <Legend {...legendProps} />}
                        {hSeries.map((s, i) => (
                            <Bar key={s} dataKey={s} name={s} fill={CHART_COLORS[i % CHART_COLORS.length]} stackId={isPivoted ? 'stack' : undefined} hide={hiddenSeries.has(s)} isAnimationActive={isAnimationActive} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- AREA / STACKED_AREA ---
    if (['area', 'stacked_area'].includes(chart_type)) {
        const useStack = chart_type === 'stacked_area';
        return (
            <div style={{ width: '100%', height: 500, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <AreaChart data={data} margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey={x_axis} tick={{ fontSize: 10, fill: '#666' }} tickFormatter={formatXAxis} height={60} interval={0} angle={-45} textAnchor="end" tickMargin={10} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70} />
                        <Tooltip formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} wrapperStyle={{ pointerEvents: 'auto' }} />
                        {showLegend && <Legend {...legendProps} />}
                        {series.map((s, i) => (
                            <Area key={s} type="monotone" dataKey={s} name={s} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.4} stackId={useStack ? 'stack' : undefined} hide={hiddenSeries.has(s)} isAnimationActive={isAnimationActive} />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- PIE / DONUT ---
    if (['pie', 'donut'].includes(chart_type)) {
        const innerRadius = chart_type === 'donut' ? '50%' : 0;
        return (
            <div style={{ width: '100%', height: 450, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey={y_axis}
                            nameKey={x_axis}
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                            innerRadius={innerRadius}
                            label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(1)}%`}
                            labelLine={true}
                            isAnimationActive={isAnimationActive}
                        >
                            {data.map((item: any, i: number) => (
                                <Cell key={`pie-cell-${item[x_axis] || i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value: any) => [formatNumber(value)]} contentStyle={tooltipStyle} />
                        <Legend formatter={(value: string) => <span style={{ color: '#333', fontWeight: 500, fontSize: '11px' }}>{value}</span>} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- SCATTER ---
    if (chart_type === 'scatter') {
        return (
            <div style={{ width: '100%', height: 500, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <ScatterChart margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis type="number" dataKey={x_axis} name={x_axis} tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)}>
                            <Label value={x_axis_label || x_axis} position="insideBottom" offset={-10} style={{ fontSize: '11px', fill: '#666', fontWeight: 600 }} />
                        </XAxis>
                        <YAxis type="number" dataKey={y_axis} name={y_axis} tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70}>
                            <Label value={y_axis_label || y_axis} angle={-90} position="center" style={{ fontSize: '12px', fill: '#444', fontWeight: 600, textAnchor: 'middle' }} dx={-30} />
                        </YAxis>
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} />
                        <Scatter data={data} fill={CHART_COLORS[0]} isAnimationActive={isAnimationActive} />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- COMBO (dual-axis) ---
    if (chart_type === 'combo' && y2_axis) {
        return (
            <div style={{ width: '100%', height: 500, marginTop: '1rem', position: 'relative', minWidth: 0, overflow: 'hidden' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '4px' }}>{chart_title}</div>}
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={data} margin={margin}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey={x_axis} tick={{ fontSize: 10, fill: '#666' }} tickFormatter={formatXAxis} height={60} interval={0} angle={-45} textAnchor="end" tickMargin={10} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70}>
                            <Label value={y_axis_label || y_axis} angle={-90} position="center" style={{ fontSize: '12px', fill: CHART_COLORS[0], fontWeight: 600, textAnchor: 'middle' }} dx={-30} />
                        </YAxis>
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} width={70}>
                            <Label value={y2_axis_label || y2_axis} angle={90} position="center" style={{ fontSize: '12px', fill: CHART_COLORS[1], fontWeight: 600, textAnchor: 'middle' }} dx={30} />
                        </YAxis>
                        <Tooltip formatter={(value: any, name: string | undefined) => [formatNumber(value), name || '']} contentStyle={tooltipStyle} wrapperStyle={{ pointerEvents: 'auto' }} />
                        <Legend {...legendProps} />
                        <Bar yAxisId="left" dataKey={y_axis} name={y_axis} fill={CHART_COLORS[0]} fillOpacity={0.7} hide={hiddenSeries.has(y_axis)} isAnimationActive={isAnimationActive} />
                        <Line yAxisId="right" type="monotone" dataKey={y2_axis} name={y2_axis} stroke={CHART_COLORS[1]} strokeWidth={2} dot={{ r: 3 }} hide={hiddenSeries.has(y2_axis)} isAnimationActive={isAnimationActive} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    }

    // --- HEATMAP (REWORKED) ---
    if (chart_type === 'heatmap') {
        const xKey = x_axis || '';
        const yKey = y_axis || '';
        const valKey = color_column || '';

        // Extract unique sorted categories
        const xValues = Array.from(new Set(data.map(d => String(d[xKey] || '')))).sort();
        const yValues = Array.from(new Set(data.map(d => String(d[yKey] || '')))).sort();

        const values = data.map(d => Number(d[valKey])).filter(v => !isNaN(v));
        const minVal = values.length ? Math.min(...values) : 0;
        const maxVal = values.length ? Math.max(...values) : 1;

        const getColor = (val: any) => {
            const v = Number(val);
            if (isNaN(v)) return '#f8f9fa';
            const ratio = Math.max(0, Math.min(1, (v - minVal) / (maxVal - minVal || 1)));
            // Using a nice sequential blue scale: #ebf3fb (light) to #003366 (IESO Blue)
            const r = Math.floor(235 - ratio * (235 - 0));
            const g = Math.floor(243 - ratio * (243 - 51));
            const b = Math.floor(251 - ratio * (251 - 102));
            return `rgb(${r}, ${g}, ${b})`;
        };

        const getValue = (x: string, y: string) => {
            const match = data.find(d => String(d[xKey]) === x && String(d[yKey]) === y);
            return match ? match[valKey] : null;
        };

        return (
            <div style={{ width: '100%', marginTop: '1rem', position: 'relative', background: '#fff', padding: '20px 10px', minHeight: '400px' }}>
                {chart_title && <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '15px', color: '#1a1a1a', marginBottom: '24px' }}>{chart_title}</div>}

                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowX: 'auto' }}>
                    <div style={{ display: 'flex', minWidth: 'fit-content' }}>
                        {/* Y Axis Label */}
                        <div style={{ width: '30px', position: 'relative' }}>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) rotate(-90deg)',
                                whiteSpace: 'nowrap',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#444'
                            }}>
                                {y_axis_label || yKey}
                            </div>
                        </div>

                        {/* Heatmap Grid */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {yValues.map((yVal) => (
                                <div key={yVal} style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                                    {/* Y Ticks */}
                                    <div style={{
                                        width: '120px',
                                        textAlign: 'right',
                                        paddingRight: '12px',
                                        fontSize: '10px',
                                        color: '#666',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }} title={yVal}>
                                        {yVal}
                                    </div>

                                    {/* Cells Row */}
                                    <div style={{ display: 'flex', flex: 1, gap: '2px', height: '100%' }}>
                                        {xValues.map((xVal) => {
                                            const val = getValue(xVal, yVal);
                                            const bgColor = val !== null ? getColor(val) : '#f8f9fa';
                                            return (
                                                <div
                                                    key={`${xVal}-${yVal}`}
                                                    title={`${xVal} / ${yVal}: ${formatNumber(val)}`}
                                                    style={{
                                                        flex: 1,
                                                        height: '100%',
                                                        backgroundColor: bgColor,
                                                        borderRadius: '2px',
                                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                                        cursor: 'pointer',
                                                        border: '0.5px solid rgba(0,0,0,0.05)',
                                                        minWidth: '40px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        const target = e.currentTarget as HTMLElement;
                                                        target.style.transform = 'scale(1.05)';
                                                        target.style.zIndex = '5';
                                                        target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const target = e.currentTarget as HTMLElement;
                                                        target.style.transform = 'scale(1)';
                                                        target.style.zIndex = '1';
                                                        target.style.boxShadow = 'none';
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {/* X Axis Labels */}
                            <div style={{ display: 'flex', marginLeft: '120px', marginTop: '4px' }}>
                                <div style={{ display: 'flex', flex: 1, gap: '2px' }}>
                                    {xValues.map((xVal) => (
                                        <div key={xVal} style={{ flex: 1, textAlign: 'center', minWidth: '40px' }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#666',
                                                fontWeight: 500,
                                                transform: 'rotate(-45deg)',
                                                transformOrigin: 'top center',
                                                whiteSpace: 'nowrap',
                                                marginTop: '12px',
                                                display: 'inline-block'
                                            }}>
                                                {formatXAxis(xVal)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* X Axis Main Label */}
                            <div style={{ textAlign: 'center', marginTop: '60px', fontSize: '11px', fontWeight: 600, color: '#444' }}>
                                {x_axis_label || xKey}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legend */}
                <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {valKey}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>{formatNumber(minVal)}</span>
                        <div style={{
                            width: '160px',
                            height: '10px',
                            background: `linear-gradient(to right, ${getColor(minVal)}, ${getColor(maxVal)})`,
                            borderRadius: '5px',
                            border: '1px solid #eee',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                        }}></div>
                        <span style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>{formatNumber(maxVal)}</span>
                    </div>
                </div>
            </div>
        );
    }
    // --- FALLBACK: table or unknown ---
    return null;
});

type TableAction =
    | { type: 'SET_DATA', rowCount: number, tableData: any[], columns: string[], chartData: any[], chartColumns: string[] }
    | { type: 'SET_VIEW', view: 'table' | 'chart' }
    | { type: 'SET_COPIED', copied: boolean }
    | { type: 'SET_DOWNLOADING', downloading: boolean }
    | { type: 'SET_CHART_DOWNLOADING', chartDownloading: boolean }
    | { type: 'SORT_DATA', key: string };

interface TableState {
    rowCount: number;
    copied: boolean;
    downloading: boolean;
    chartDownloading: boolean;
    view: 'table' | 'chart';
    tableData: any[];
    originalColumns: string[]; // Store original columns for display
    chartData: any[]; // Data specifically for charting (pivoted)
    chartColumns: string[]; // Columns specifically for charting
    sortConfig: { key: string, direction: 'asc' | 'desc' } | null;
}

const tableReducer = (state: TableState, action: TableAction): TableState => {
    switch (action.type) {
        case 'SET_DATA':
            return {
                ...state,
                rowCount: action.rowCount,
                tableData: action.tableData,
                originalColumns: action.columns,
                chartData: action.chartData,
                chartColumns: action.chartColumns,
                sortConfig: null // Reset sort on new data
            };
        case 'SET_VIEW':
            return { ...state, view: action.view };
        case 'SET_COPIED':
            return { ...state, copied: action.copied };
        case 'SET_DOWNLOADING':
            return { ...state, downloading: action.downloading };
        case 'SET_CHART_DOWNLOADING':
            return { ...state, chartDownloading: action.chartDownloading };
        case 'SORT_DATA': {
            let direction: 'asc' | 'desc' = 'asc';
            if (state.sortConfig?.key === action.key && state.sortConfig.direction === 'asc') {
                direction = 'desc';
            }

            const sortedData = [...state.tableData].sort((a, b) => {
                const aVal = a[action.key];
                const bVal = b[action.key];

                // Helper to determine if a value is considered "empty" or "null"
                const isEmpty = (val: any) => {
                    if (val === null || val === undefined || val === '') return true;
                    if (typeof val === 'string' && val.trim().toUpperCase() === 'NULL') return true;
                    return false;
                };

                const aEmpty = isEmpty(aVal);
                const bEmpty = isEmpty(bVal);

                // Both empty: keep original order
                if (aEmpty && bEmpty) return 0;
                // A is empty: push to bottom
                if (aEmpty) return 1;
                // B is empty: push to bottom
                if (bEmpty) return -1;

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return direction === 'asc' ? aVal - bVal : bVal - aVal;
                }

                const sA = String(aVal).toLowerCase();
                const sB = String(bVal).toLowerCase();
                if (sA < sB) return direction === 'asc' ? -1 : 1;
                if (sA > sB) return direction === 'asc' ? 1 : -1;
                return 0;
            });

            return {
                ...state,
                tableData: sortedData,
                sortConfig: { key: action.key, direction }
            };
        }
        default:
            return state;
    }
};

const TableRenderer: FC<TableRendererProps> = memo(({ children, messageId, attachmentId, chartRecommendation }) => {
    const tableRef = useRef<HTMLTableElement>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const { getDownloadUrl } = useGenie();
    const [isPending, startTransition] = useTransition();

    const [state, dispatch] = useReducer(tableReducer, {
        rowCount: 0,
        copied: false,
        downloading: false,
        chartDownloading: false,
        view: 'table',
        tableData: [],
        originalColumns: [],
        chartData: [],
        chartColumns: [],
        sortConfig: null
    });

    const hasChart = !!chartRecommendation && chartRecommendation.chart_type !== 'table';
    const { rowCount, copied, downloading, chartDownloading, view, tableData, originalColumns, chartData, chartColumns, sortConfig } = state;

    useEffect(() => {
        if (tableRef.current) {
            const table = tableRef.current;
            const ths = Array.from(table.querySelectorAll('thead th'));
            const colNames = ths.map(th => (th as HTMLElement).innerText.trim());
            const trs = Array.from(table.querySelectorAll('tbody tr'));

            const rawData = trs.map(tr => {
                const tds = Array.from(tr.querySelectorAll('td'));
                const row: Record<string, any> = {};
                tds.forEach((td, i) => {
                    const name = colNames[i];
                    if (!name) return;
                    const val = (td as HTMLElement).innerText.trim();
                    const cleanVal = val.replace(/,/g, '').replace(/%/g, '');
                    const isNumber = /^-?\d+(\.\d+)?$/.test(cleanVal);
                    const num = parseFloat(cleanVal);
                    row[name] = isNumber ? num : val;
                });
                return row;
            });

            // Prepare Table Display Data
            const displayData = [...rawData];
            const displayCols = [...colNames];

            // Prepare Chart Data — driven by backend recommendation
            let finalChartData = rawData;
            let finalChartCols = colNames;

            if (chartRecommendation && chartRecommendation.color_column && !['table', 'heatmap'].includes(chartRecommendation.chart_type)) {
                // Pivot data when color_column is specified (multi-series)
                const xCol = chartRecommendation.x_axis;
                const colorCol = chartRecommendation.color_column;
                const valueCol = chartRecommendation.y_axis;

                if (colNames.includes(xCol) && colNames.includes(colorCol) && colNames.includes(valueCol)) {
                    const uniqueCategories = Array.from(new Set(rawData.map(r => String(r[colorCol])))).filter(v => v !== 'null' && v !== 'undefined').sort();

                    if (uniqueCategories.length > 1 && uniqueCategories.length < 150) {
                        const pivoted: Record<string, any>[] = [];
                        const labelGroups: Record<string, any> = {};

                        rawData.forEach(row => {
                            const l = String(row[xCol]);
                            const c = String(row[colorCol]);
                            const v = row[valueCol] || 0;

                            if (!labelGroups[l]) {
                                labelGroups[l] = { [xCol]: row[xCol] };
                                pivoted.push(labelGroups[l]);
                            }
                            labelGroups[l][c] = (labelGroups[l][c] || 0) + (typeof v === 'number' ? v : 0);
                        });

                        finalChartData = pivoted.sort((a, b) => String(a[xCol]).localeCompare(String(b[xCol])));
                        finalChartCols = [xCol, ...uniqueCategories];
                    }
                }
            }

            dispatch({
                type: 'SET_DATA',
                rowCount: trs.length,
                tableData: displayData,
                columns: displayCols,
                chartData: finalChartData,
                chartColumns: finalChartCols,
            });
        }
    }, [children, chartRecommendation]);

    const handleDownload = async () => {
        if (!messageId || !attachmentId) return;
        dispatch({ type: 'SET_DOWNLOADING', downloading: true });
        await getDownloadUrl(messageId, attachmentId);
        dispatch({ type: 'SET_DOWNLOADING', downloading: false });
    };

    const handleCopy = () => {
        if (tableData.length > 0) {
            let markdown = `| ${originalColumns.join(' | ')} |\n| ${originalColumns.map(() => '---').join(' | ')} |\n`;
            tableData.forEach(row => {
                markdown += `| ${originalColumns.map(col => String(row[col] ?? '').replace(/\|/g, '\\|')).join(' | ')} |\n`;
            });

            const tableHtml = `<table><thead><tr>${originalColumns.map(c => `<th>${c}</th>`).join('')}</tr></thead><tbody>${tableData.map(r => `<tr>${originalColumns.map(c => `<td>${r[c] ?? ''}</td>`).join('')}</tr>`).join('')}</tbody></table>`;

            try {
                const clipboardData = {
                    'text/plain': new Blob([markdown], { type: 'text/plain' }),
                    'text/html': new Blob([tableHtml], { type: 'text/html' })
                };
                navigator.clipboard.write([new ClipboardItem(clipboardData)]).then(() => {
                    dispatch({ type: 'SET_COPIED', copied: true });
                    setTimeout(() => dispatch({ type: 'SET_COPIED', copied: false }), 2000);
                });
            } catch (err) {
                navigator.clipboard.writeText(markdown).then(() => {
                    dispatch({ type: 'SET_COPIED', copied: true });
                    setTimeout(() => dispatch({ type: 'SET_COPIED', copied: false }), 2000);
                });
            }
        }
    };

    const toggleSort = (key: string) => {
        startTransition(() => {
            dispatch({ type: 'SORT_DATA', key });
        });
    };

    const formatCellValue = (val: any, colName?: string) => {
        if (typeof val === 'number') {
            if (isLabelColumn(colName)) return val.toString();
            return val.toLocaleString();
        }
        return val;
    };

    const handleChartDownload = async () => {
        if (!chartRef.current) return;
        dispatch({ type: 'SET_CHART_DOWNLOADING', chartDownloading: true });

        // Give a tick for React to re-render with isAnimationActive=false
        await new Promise(r => setTimeout(r, 100));

        try {
            const canvas = await html2canvas(chartRef.current, {
                scale: 2,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false,
                onclone: (clonedDoc) => {
                    const wrapper = clonedDoc.querySelector('.genie-chart-wrapper');
                    if (wrapper) {
                        (wrapper as HTMLElement).style.animation = 'none';
                        (wrapper as HTMLElement).style.transition = 'none';
                        (wrapper as HTMLElement).style.opacity = '1';
                    }
                }
            });
            canvas.toBlob((blob) => {
                if (!blob) return;
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                const title = chartRecommendation?.chart_title || 'chart';
                link.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            }, 'image/png');
        } catch (err) {
            console.error('Chart download failed:', err);
        } finally {
            dispatch({ type: 'SET_CHART_DOWNLOADING', chartDownloading: false });
        }
    };

    return (
        <div id={`genie-table-${messageId}`} className="genie-table-container" style={{ opacity: isPending ? 0.7 : 1, transition: 'opacity 0.2s', maxWidth: '100%' }}>
            <div className="genie-table-header">
                <div className="genie-table-title">
                    <i className={`fa-solid ${view === 'table' ? 'fa-table' : 'fa-chart-column'}`}></i>
                    {view === 'table' ? `Result table (${rowCount} rows)` : 'Chart View'}
                </div>
                <div className="genie-table-actions">
                    {hasChart && (
                        <button
                            className={`copy-table-btn-header ${view === 'chart' ? 'active' : ''}`}
                            title={view === 'table' ? "Show chart" : "Show table"}
                            onClick={() => startTransition(() => dispatch({ type: 'SET_VIEW', view: view === 'table' ? 'chart' : 'table' }))}>
                            <i className={`fa-solid ${view === 'table' ? 'fa-chart-column' : 'fa-table'}`}></i>
                        </button>
                    )}
                    <button
                        className="copy-table-btn-header"
                        title={copied ? "Copied!" : "Copy table"}
                        onClick={handleCopy}>
                        <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                    <button
                        className="copy-table-btn-header"
                        title="Download (CSV)"
                        onClick={handleDownload}
                        disabled={downloading || !messageId || !attachmentId}>
                        {downloading ? (
                            <i className="fa-solid fa-spinner fa-spin fa-fw"></i>
                        ) : (
                            <i className="fa-solid fa-download fa-fw"></i>
                        )}
                    </button>
                    {view === 'chart' && hasChart && (
                        <button
                            className="copy-table-btn-header"
                            title="Download chart as image"
                            onClick={handleChartDownload}
                            disabled={chartDownloading}>
                            {chartDownloading ? (
                                <i className="fa-solid fa-spinner fa-spin fa-fw"></i>
                            ) : (
                                <i className="fa-solid fa-image fa-fw"></i>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Hidden source table for data extraction */}
            <div style={{ display: 'none' }} aria-hidden="true">
                <table ref={tableRef} className="table table-hover table-sm mb-0">
                    {children}
                </table>
            </div>

            <div className="genie-table-scroll-wrapper" style={{
                maxHeight: rowCount > 15 ? '450px' : 'none',
                overflowY: rowCount > 15 ? 'auto' : 'visible',
                overflowX: 'auto',
                display: view === 'table' ? 'block' : 'none',
                position: 'relative',
                maxWidth: '100%',
                scrollbarGutter: 'stable'
            }}>
                <table className="table table-hover table-sm mb-0 genie-rendered-table" style={{ width: '100%', minWidth: 'max-content' }}>
                    <thead>
                        <tr>
                            {originalColumns.map((col) => {
                                return (
                                    <th
                                        key={col}
                                        onClick={() => toggleSort(col)}
                                        style={{
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            backgroundColor: '#8CD2F3',
                                            paddingRight: col === originalColumns[originalColumns.length - 1] ? '2.5rem' : '0.75rem'
                                        }}
                                        className="genie-sortable-header"
                                    >
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span>{col}</span>
                                            <i className={`fa-solid ${sortConfig?.key === col ? (sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'} opacity-50`}></i>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, i) => (
                            <tr key={messageId ? `${messageId}-row-${i}` : i}>
                                {originalColumns.map((col) => {
                                    return (
                                        <td
                                            key={col}
                                            style={{
                                                textAlign: typeof row[col] === 'number' ? 'right' : 'left',
                                                paddingRight: col === originalColumns[originalColumns.length - 1] ? '2.5rem' : '0.75rem'
                                            }}
                                        >
                                            {formatCellValue(row[col], col)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {view === 'chart' && hasChart && chartRecommendation && (
                <div ref={chartRef} className="genie-chart-wrapper p-3 bg-white border-top" style={{ overflowX: 'auto', minWidth: 0, maxWidth: '100%' }}>
                    <ChartRenderer data={chartData} columns={chartColumns} recommendation={chartRecommendation} isAnimationActive={!chartDownloading} />
                </div>
            )}
        </div>
    );
});

interface GenieMessageContentProps {
    content: string;
    messageId?: string;
    attachmentId?: string;
    chartRecommendation?: ChartRecommendation;
}

const GenieMessageContent: FC<GenieMessageContentProps> = memo(({ content, messageId, attachmentId, chartRecommendation }) => {
    const components = useMemo(() => ({
        table: ({ node, ...props }: any) => (
            <TableRenderer
                {...props}
                messageId={messageId}
                attachmentId={attachmentId}
                chartRecommendation={chartRecommendation}
            />
        )
    }), [messageId, attachmentId, chartRecommendation]);

    return (
        <div className="markdown-body">
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={components}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
});


const Genie = () => {
    const { messages, isThinking, thinkingStage, sendMessage, submitFeedback, restartConversation } = useGenie();
    const [inputValue, setInputValue] = useState('');
    const chatBodyRef = useRef<HTMLDivElement>(null);
    const prevMessagesLength = useRef(0);
    const [feedbackStatus, setFeedbackStatus] = useState<Record<string, 'up' | 'down'>>({});

    const handleFeedback = async (messageId: string, isUseful: boolean) => {
        if (!messageId) return;
        setFeedbackStatus(prev => ({
            ...prev,
            [messageId]: isUseful ? 'up' : 'down'
        }));
        await submitFeedback(messageId, isUseful);
    };

    const sampleQuestions = [
        "Explain the dataset(s) you have access to.",
        "What are the different DRCs?",
        "What is the total consumption in kWh for each IESO zone for the last month?"
    ];

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    useEffect(() => {
        if (!chatBodyRef.current) return;

        const scrollContainer = chatBodyRef.current;
        const currentLength = messages.length;
        const isNewMessage = currentLength > prevMessagesLength.current;

        if (isNewMessage) {
            const lastMessage = messages[currentLength - 1];
            if (lastMessage.type === 'genie') {
                const elements = scrollContainer.querySelectorAll('.genie-message');
                const lastElement = elements[elements.length - 1] as HTMLElement;
                if (lastElement) {
                    scrollContainer.scrollTop = lastElement.offsetTop - 80;
                }
            } else {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        } else if (isThinking) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }

        prevMessagesLength.current = currentLength;
    }, [messages, isThinking]);

    const handleSendMessage = async (text: string) => {
        if (!text.trim()) return;
        setInputValue('');
        await sendMessage(text);
    };

    const handleRestart = async () => {
        await restartConversation();
    };

    return (
        <div id="genie" className="d-flex flex-column" style={{ display: 'flex', height: '100%', maxHeight: '100%', overflow: 'hidden' }}>
            <div className="chat-body flex-grow-1" ref={chatBodyRef}>
                <div className="chat-header d-flex justify-content-between align-items-center" style={{ flexShrink: 0, zIndex: 10 }}>
                    <h5 className="m-0"><i className="fa-solid fa-wand-magic-sparkles me-2"></i>Genie Assistant</h5>
                    <button id="restart-chat" className="btn btn-outline-secondary btn-sm" onClick={handleRestart} title="Restart Conversation">
                        <i className="fa fa-refresh"></i> Restart
                    </button>
                </div>
                <div className="chat-content">
                    <div className="genie-welcome-message chat-fade-in">
                        <div className="genie-welcome-header">
                            <div className="genie-welcome-header-content">
                                <div className="genie-avatar-large">
                                    <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#003366', fontSize: '1.8rem' }}></i>
                                </div>
                                <div>
                                    <h1 className="genie-welcome-title">Hi, I'm Genie!</h1>
                                    <p className="genie-welcome-subtitle">Ask me questions about SME data, and I'll do my best to help.</p>
                                </div>
                            </div>
                            <div className="genie-experimental-badge">
                                <i className="fa fa-flask"></i>
                                <span><strong>Note:</strong> Genie is an experimental AI assistant and currently in testing mode.</span>
                            </div>
                        </div>

                        <div className="genie-welcome-body">
                            <div className="genie-sample-section" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                                <span className="genie-sample-section-title">Try a sample question:</span>
                                <div className="genie-sample-questions-grid">
                                    {sampleQuestions.map(q => (
                                        <button key={q} className="genie-sample-card" onClick={() => handleSendMessage(q)} disabled={isThinking}>
                                            <i className="fa-regular fa-comment-dots"></i>
                                            <span>{q}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="chat-timestamp text-left" style={{ fontSize: '0.85em', color: '#718096', marginTop: '1.2rem' }}>
                                {formatTime(new Date())}
                            </div>
                        </div>
                    </div>

                    {messages.map(msg => (
                        <div key={msg.messageId} className={`${msg.type === 'user' ? 'user-message' : 'genie-message'} chat-fade-in`} style={{ position: 'relative' }}>
                            {msg.type === 'user' ? (
                                <div>{msg.content}</div>
                            ) : (
                                <GenieMessageContent
                                    content={msg.content}
                                    messageId={msg.messageId}
                                    attachmentId={msg.attachmentId}
                                    chartRecommendation={msg.chartRecommendation}
                                />
                            )}

                            {msg.type === 'genie' && msg.suggestedQuestions && msg.suggestedQuestions.length > 0 && (
                                <div className="genie-suggested-questions" style={{ marginTop: '1em' }}>
                                    <span style={{ fontSize: '0.9em', fontWeight: 600, color: '#003366', opacity: 0.8 }}>Follow-up suggestions:</span>
                                    <div className="genie-sample-questions" style={{ marginTop: '0.5em' }}>
                                        {msg.suggestedQuestions.map(q => (
                                            <button key={q} className="genie-sample-question" onClick={() => handleSendMessage(q)} disabled={isThinking}>
                                                <i className="fa-regular fa-comment-dots me-1"></i>{q}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <div className={`chat-timestamp text-${msg.type === 'user' ? 'right' : 'left'}`}
                                    style={{ fontSize: '0.85em', color: msg.type === 'user' ? '#ffe699' : '#003366', opacity: 0.7 }}>
                                    {msg.timestamp}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {msg.type === 'user' && <CopyButton text={msg.content} />}
                                    {msg.type === 'genie' && msg.messageId && (
                                        <div className="genie-feedback-btns" style={{ display: 'flex', gap: '8px' }}>
                                            <CopyButton text={msg.content} />
                                            <button
                                                className={`btn btn-link p-0 ${feedbackStatus[msg.messageId] === 'up' ? 'text-success' : 'text-secondary'}`}
                                                onClick={() => handleFeedback(msg.messageId!, true)}
                                                style={{ fontSize: '0.9em', opacity: feedbackStatus[msg.messageId] === 'up' ? 1 : 0.6 }}
                                                title="Helpful">
                                                <i className="fa-solid fa-thumbs-up"></i>
                                            </button>
                                            <button
                                                className={`btn btn-link p-0 ${feedbackStatus[msg.messageId] === 'down' ? 'text-danger' : 'text-secondary'}`}
                                                onClick={() => handleFeedback(msg.messageId!, false)}
                                                style={{ fontSize: '0.9em', opacity: feedbackStatus[msg.messageId] === 'down' ? 1 : 0.6 }}
                                                title="Not helpful">
                                                <i className="fa-solid fa-thumbs-down"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {isThinking && (
                        <div className="genie-message thinking-indicator chat-fade-in">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {thinkingStage ? (
                                    <div className="thinking-stage-text" style={{ fontSize: '0.9em', color: '#003366', fontWeight: 500, opacity: 0.8 }}>
                                        {thinkingStage}
                                    </div>
                                ) : null}
                                <span className="thinking-dots" style={{ margin: 0 }}>
                                    <span></span><span></span><span></span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="chat-footer p-3 border-top bg-white" style={{ flexShrink: 0 }}>
                <form id="chat-form" className="d-flex" onSubmit={(e) => { e.preventDefault(); handleSendMessage(inputValue); }}>
                    <input
                        type="text"
                        id="chat-input"
                        className="form-control me-2"
                        placeholder="Ask a question..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        disabled={isThinking}
                        autoComplete="off"
                    />
                    <button type="submit" className="btn btn-chat" disabled={isThinking || !inputValue.trim()}>
                        <i className="fa-solid fa-paper-plane"></i>
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Genie;